import {
    AutoAwesome,
    BusinessOutlined,
    ContentCopy,
    NotificationsNoneOutlined,
    PersonOutline,
    ScheduleOutlined,
    SvgIconComponent,
    UserOutlined,
} from 'components/icons';
import { useCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { useDebtReminderModal } from 'components/modals/invoices/DebtReminderModal';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { createContext, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { isAiChatRecordPath } from 'pages/ai-chat/components/message/aiChatLinkResolver';
import { useDispatch } from 'store';
import { openFloatingAiWithPrompt } from 'store/reducers/aiChat';
import {
    GlobalSearchPeekCard as GlobalSearchPeekCardDto,
    GlobalSearchStatusTone,
    GlobalSearchEntityType,
} from 'types/globalSearch';
import { errorAlert, successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { absoluteUrlForResult, pathForResult } from 'utils/globalSearchRoutes';
import { GLOBAL_SEARCH_ENTITY_ICON } from './entityDisplay';

/**
 * How each tone is coloured, in the same tokens the list pages' own status chips use.
 *
 * The backend sends a label and a tone and never a colour, so this is the only place in the
 * frontend that decides what a status looks like — and it decides it once for every family rather
 * than per family, which is what makes a card for a family added later need no change here.
 */
const TONE_CLASS: Record<GlobalSearchStatusTone, string> = {
    SUCCESS: 'bg-success-lighter text-fg-success',
    WARNING: 'bg-warning-lighter text-fg-warning',
    ERROR: 'bg-error-lighter text-fg-error',
    INFO: 'bg-info-lighter text-link',
    NEUTRAL: 'bg-grey-200 text-grey-a400',
};

/**
 * The glyphs for the icon keys that are not an entity family.
 *
 * A family's key is its own name and resolves through {@link GLOBAL_SEARCH_ENTITY_ICON}; a family
 * whose records come in kinds says which kind instead, and those are the keys here.
 */
const KIND_ICON: Record<string, SvgIconComponent> = {
    PARTY_PERSON: PersonOutline,
    PARTY_COMPANY: BusinessOutlined,
};

/** What the card can draw for one action kind: a glyph, its wording, and what pressing it does. */
interface PeekAction {
    icon: SvgIconComponent;
    labelId: string;
    run: () => void;
}

/**
 * Puts the card away, and the search surface it hangs off with it when there is one. Set by a surface whose page
 * can host the create dialog: an action that opens it changes no page, so nothing else would take the card off the
 * screen.
 */
export const SearchCloseContext = createContext<() => void>(() => undefined);

/** The reminder flow is an invoice's; a prepayment is one too, and is read at the same route. */
const SEND_REMINDER_FAMILIES = new Set<GlobalSearchEntityType>(['INVOICE', 'PREPAYMENT']);

/**
 * The question the assistant is handed, with the record named as a link the reader can follow.
 *
 * The wording is the family's own — "the invoice", "the case" — and falls back to the family-free
 * phrasing where a family has none yet, so a family added later reads plainly rather than not at
 * all. The record itself goes in as markdown: backticked, so it reads as the record's name and not
 * as part of the sentence, and linked to its own page where that page exists — a pricelist has
 * none, so it goes in unlinked. Markdown's own punctuation is stripped from the name so it cannot
 * break the link out; whether the record links is the path's question alone.
 */
function aiSummaryPrompt(intl: ReturnType<typeof useIntl>, card: GlobalSearchPeekCardDto): string {
    const familyId = `global-search.peek.ai-prompt.${card.entityType}`;
    const id = familyId in intl.messages ? familyId : 'global-search.peek.ai-prompt.default';
    const name = card.title.replaceAll(/[`[\]]/g, '');
    const path = pathForResult(card.entityType, card.id);
    const record = isAiChatRecordPath(path) ? `[\`${name}\`](${path})` : `\`${name}\``;
    return intl.formatMessage({ id }, { record });
}

/**
 * One record's card, as the hover peek and the narrow sheet draw it.
 *
 * Everything on it arrives formatted — the money figures carry their currency, the dates the
 * portal's format, the status its own wording — so nothing here reformats a value or translates a
 * label. The two things this side owns are the glyph and the colour of the status.
 */
export function GlobalSearchPeekCard({ card }: { card: GlobalSearchPeekCardDto }) {
    const intl = useIntl();
    const actions = usePeekActions(card);
    const Icon = KIND_ICON[card.iconKey] ?? GLOBAL_SEARCH_ENTITY_ICON[card.iconKey as GlobalSearchEntityType];

    return (
        <>
            <div className="flex items-start gap-2.5 px-3.5 pt-3">
                <span className="bg-primary-surface text-primary flex size-7 flex-none items-center justify-center rounded-[8px]">
                    {Icon && <Icon aria-hidden className="size-4" />}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-grey-700 text-sm leading-[1.43] font-semibold text-pretty">{card.title}</div>
                    {card.subtitle && <div className="text-grey-500 mt-0.5 text-xs leading-[1.5]">{card.subtitle}</div>}
                </div>
                {card.status && (
                    <Badge
                        variant="outline"
                        className={cn(
                            'text-caption h-4 flex-none rounded-[8px] border-transparent px-1.5 leading-[1.3333] font-semibold',
                            TONE_CLASS[card.status.tone]
                        )}
                    >
                        {card.status.label}
                    </Badge>
                )}
            </div>
            {card.description && (
                // A record's notes can run to pages, and the card is a glance: it keeps the first lines and
                // the record the card hangs off holds the rest. `wrap-anywhere` keeps a long URL or email in
                // the notes from pushing past the card's edge.
                <p className="text-grey-600 m-0 line-clamp-4 px-3.5 pt-2.5 text-xs leading-[1.5] text-pretty wrap-anywhere">
                    {card.description}
                </p>
            )}
            {card.facts.length > 0 && (
                <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-2.5 px-3.5 py-3">
                    {card.facts.map((fact) => (
                        <div key={fact.label} className="min-w-0">
                            <dt className="text-grey-500 text-caption leading-[1.3333]">{fact.label}</dt>
                            <dd className="text-grey-700 text-meta m-0 truncate leading-[1.54] font-semibold">
                                {fact.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
            {actions.length > 0 && (
                <div className="border-grey-200 bg-grey-50 new:bg-surface-subtle flex flex-wrap gap-1.5 border-t px-3.5 py-2.5">
                    {actions.map((action) => (
                        <Button
                            key={action.labelId}
                            variant="outline"
                            size="sm"
                            onClick={action.run}
                            // The footer's buttons are the card's only controls, so they are the
                            // only thing on it the touch-target rule reaches. They relax to the
                            // design's height once there is a pointer to hit them with.
                            className="border-grey-300 new:border-border-control text-grey-700 bg-paper h-auto min-h-11 gap-1.5 rounded-[8px] px-2.5 py-1 text-xs leading-[1.5] font-semibold sm:min-h-[30px]"
                        >
                            <action.icon aria-hidden className="size-[13px]" />
                            {intl.formatMessage({ id: action.labelId })}
                        </Button>
                    ))}
                </div>
            )}
        </>
    );
}

/**
 * The actions the card draws, from the kinds the backend says currently apply.
 *
 * A kind this build cannot carry out is dropped rather than drawn: the backend decides what applies
 * to the record, this side decides what it can do about it, and a button that does nothing is worse
 * than an absent one. That is also why an unrecognised kind — one a newer backend offers — costs
 * nothing here, and why the kinds that belong to one family check the family before drawing.
 */
function usePeekActions(card: GlobalSearchPeekCardDto): PeekAction[] {
    const intl = useIntl();
    const navigate = useNavigate();
    const createDialog = useCreateDialog();
    const closeSearch = useContext(SearchCloseContext);
    const dispatch = useDispatch();
    // The record's own id is the whole of what the reminder needs: the modal asks for the rest.
    // One thing does not survive the trip — picking the "all company invoices" scope names the
    // company off the invoice row, which a card does not carry, so that line reads "-" here.
    const { openDebtReminderModal } = useDebtReminderModal([card.id]);

    const copyLink = () => {
        navigator.clipboard.writeText(absoluteUrlForResult(card.entityType, card.id)).then(
            () => successAlert(intl.formatMessage({ id: 'copied-to-clipboard' })),
            () => errorAlert(intl.formatMessage({ id: 'technical-error' }))
        );
    };

    // The only route that opens a timecard form on a record is the case's own.
    const logTime = () => {
        const url = `/matter/${card.id}/timecards/new`;
        if (createDialog?.open(url)) {
            closeSearch();
        } else {
            navigate(url);
        }
    };

    return card.actions
        .map((kind): PeekAction | null => {
            switch (kind) {
                case 'COPY_LINK':
                    return { icon: ContentCopy, labelId: 'global-search.peek.copy-link', run: copyLink };
                case 'AI_SUMMARY':
                    return {
                        icon: AutoAwesome,
                        labelId: 'global-search.peek.ai-summary',
                        run: () => dispatch(openFloatingAiWithPrompt(aiSummaryPrompt(intl, card))),
                    };
                case 'LOG_TIME':
                    return card.entityType === 'MATTER'
                        ? { icon: ScheduleOutlined, labelId: 'global-search.peek.log-time', run: logTime }
                        : null;
                case 'SEND_REMINDER':
                    return SEND_REMINDER_FAMILIES.has(card.entityType)
                        ? {
                              icon: NotificationsNoneOutlined,
                              labelId: 'global-search.peek.send-reminder',
                              run: () => void openDebtReminderModal(),
                          }
                        : null;
                case 'OPEN_PROFILE':
                    return {
                        icon: UserOutlined,
                        labelId: 'global-search.peek.open-profile',
                        run: () => navigate(pathForResult(card.entityType, card.id)),
                    };
                default:
                    return null;
            }
        })
        .filter((action): action is PeekAction => action !== null);
}

/**
 * The card's shape while it loads.
 *
 * It draws the same header, figure grid and footer the real card does, so the card that replaces it
 * is about the same height and the popover does not reposition under the pointer.
 */
export function GlobalSearchPeekSkeleton() {
    return (
        <div aria-hidden>
            <div className="flex items-start gap-2.5 px-3.5 pt-3">
                <Skeleton className="bg-grey-200 size-7 flex-none rounded-[8px]" />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-[2px]">
                    <Skeleton className="bg-grey-200 h-3 w-[78%] rounded-[4px]" />
                    <Skeleton className="bg-grey-100 h-[9px] w-[52%] rounded-[4px]" />
                </div>
                <Skeleton className="bg-grey-200 h-4 w-11 flex-none rounded-[8px]" />
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 px-3.5 pt-4 pb-3">
                {[0, 1, 2, 3].map((cell) => (
                    <div key={cell} className="flex min-w-0 flex-col gap-[5px]">
                        <Skeleton className="bg-grey-100 h-2 w-[54%] rounded-[4px]" />
                        <Skeleton className="bg-grey-200 h-[11px] w-[80%] rounded-[4px]" />
                    </div>
                ))}
            </div>
            <div className="border-grey-200 bg-grey-50 new:bg-surface-subtle flex gap-1.5 border-t px-3.5 py-2.5">
                <Skeleton className="bg-grey-200 h-[30px] w-[92px] rounded-[8px]" />
                <Skeleton className="bg-grey-200 h-[30px] w-[140px] rounded-[8px]" />
            </div>
        </div>
    );
}
